/**
 * Service to validate an agency name against the BFF LookUps endpoint.
 *
 * Endpoint: GET /api/LookUps/validate-agency-name?agencyName=<name>
 *
 * Success response shape:
 * {
 *   "success": true,
 *   "data": { "isValid": true },
 *   "message": "Validation successful.",
 *   "errors": null
 * }
 *
 * Failure response shape:
 * {
 *   "success": false,
 *   "data": { "isValid": false },
 *   "message": "Unknown Agency",
 *   "errors": null
 * }
 *
 * The `data.isValid` boolean is the authoritative gate.  When `isValid` is
 * `false` the caller must display the top-level `message` to the user and
 * prevent further navigation.  On success the caller stores the user-entered
 * agency name so it is sent as `X-Agency-Name` on every subsequent API call.
 *
 * Agency names may contain hyphens (e.g. "Transit-Local", "Transit-QA").
 */

import axios, { AxiosError } from 'axios';
import { getConfig } from '../../../shared/config/AppConfig';
import { endPoints } from '../constants/endPoints';
import {
  ValidateAgencyNameResult,
  ValidateAgencyNameResponse,
} from '../screens/LoginProps';

export type ValidateAgencyNameError = 'AGENCY_NOT_FOUND' | 'USER_NOT_MAPPED' | 'UNKNOWN';

export interface ValidateAgencyNameFailure {
  isValid: false;
  errorType: ValidateAgencyNameError;
  /** The human-readable message returned by the API — show this directly to the user. */
  errorMessage?: string;
}

export type { ValidateAgencyNameResult, ValidateAgencyNameResponse };

/**
 * Validates the given agency name and returns `{ isValid }`.
 *
 * When `data.isValid` is `false` in the API response, returns a
 * `ValidateAgencyNameFailure` with `errorMessage` set to the API's top-level
 * `message` value.
 *
 * @param agencyName - The agency name the user entered (may contain hyphens).
 * @throws When the network request fails or the server returns a non-2xx status.
 */
export const validateAgencyName = async (
  agencyName: string,
): Promise<ValidateAgencyNameResult> => {
  console.log('[validateAgencyNameService] Validating agency name:', agencyName);

  // Use a plain (unauthenticated) axios instance so the request interceptor on
  // the shared axiosInstance does NOT attach any previously-stored Bearer token.
  // When a stored token is sent, the BFF validates the user against its own
  // database and returns 403 "not mapped" for accounts that exist in CIAM but
  // not in the BFF user table.  This endpoint is intentionally unauthenticated
  // (mirrors the web bootstrap() flow which calls it before OIDC login).
  try {
    const baseUrl = (getConfig()?.API_BASE_URL ?? '').replace(/\/+$/, '');
    const response = await axios.get<{
      success?: boolean;
      data?: ValidateAgencyNameResult | null;
      message?: string;
      errors?: string | null;
    }>(
      `${baseUrl}${endPoints?.auth?.agencyValidation}`,
      {
        params: { agencyName },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Agency-Name': agencyName,
          // BFF requires X-Agency-Id to identify the tenant.  The agency name
          // being validated is the tenant identifier (e.g. "transit-dev") so
          // we send it as both headers.  A plain axios instance is used here
          // (not the intercepted `api`) so the request interceptor that attaches
          // X-Agency-Id from agencyStorage does not run — we must set it manually.
          'X-Agency-Id': agencyName,
        },
      },
    );

    console.log('[validateAgencyNameService] HTTP status:', response.status);
    console.log('[validateAgencyNameService] response.data:', JSON.stringify(response.data));

    // response.data is the parsed JSON body:
    // { success, data: { isValid }, message, errors, timestamp }
    const responseBody = response.data;
    const topLevelMessage = responseBody.message ?? '';
    const result = responseBody.data;

    // `data.isValid` is the authoritative gate.  Surface the API's top-level
    // message so the caller can display it verbatim without a hardcoded override.
    if (!result || result.isValid === false) {
      console.warn(
        '[validateAgencyNameService] isValid:false — message:', topLevelMessage,
      );
      return {
        isValid: false,
        errorType: 'AGENCY_NOT_FOUND',
        errorMessage: topLevelMessage || 'Agency not found. Please check the agency name and try again.',
      } as ValidateAgencyNameFailure;
    }

    console.log('[validateAgencyNameService] isValid:true — agency name accepted');
    return result;
  } catch (err) {
    // Plain axios throws on non-2xx responses (unlike the intercepted axiosInstance
    // which swallows 403/404). Extract the status and message from AxiosError.
    const axiosErr = err as AxiosError<{ success?: boolean; data?: { isValid?: boolean }; message?: string }>;
    const status = axiosErr.response?.status;
    // axiosErr.response.data is the parsed error response body — message is top-level.
    const rawMessage = axiosErr.response?.data?.message ?? '';

    console.warn(
      '[validateAgencyNameService] Request failed — status:', status,
      'message:', rawMessage,
    );

    if (status === 404) {
      return {
        isValid: false,
        errorType: 'AGENCY_NOT_FOUND',
        errorMessage: rawMessage || 'Agency not found. Please check the agency name and try again.',
      } as ValidateAgencyNameFailure;
    }

    // Re-throw everything else (network errors, 500s, etc.) so the caller's
    // catch block can show the appropriate error modal.
    throw err;
  }
};